/**
 * CandidateBreakdownCard — the one-screen, glanceable, print-friendly card
 * view of a Candidate Breakdown. Default view on open per LOCKED 2026-05-25
 * spec; the toggle to the full briefing sits beside it.
 *
 * Render contract: consumes the typed `card_sections` block emitted by the
 * Layer 4 substrate prompt (resume-blueprint.md). Does NOT re-parse the full
 * breakdown's markdown — the typed block exists precisely so prompt prose
 * tweaks don't break the card. Schema:
 *
 *   {
 *     snapshot:        string,                            // 2–3 sentences
 *     strengths:       [ {headline, body}, x3 ],          // exactly 3
 *     friction_points: [ {headline, body}, x3 ],          // exactly 3
 *     energy_gainers:  [ string, ...3..5 ],
 *     energy_drainers: [ string, ...3..5 ],
 *     how_to_manage:   string,                            // 2–4 sentences
 *   }
 *
 * Five card sections per the locked spec:
 *   Snapshot · Strengths (3) · Friction + blind spots (3) · Energy · How to manage
 *
 * Off the card (full briefing only): role-fit, interview questions, 90-day
 * onboarding plan, Manager-Fit, red-flag situations.
 *
 * Brand discipline: hedged register throughout — "tends to", "watch for".
 * Friction-points section is NEVER labeled "weaknesses." Locked v10 component
 * grammar; design tokens from /shared/design-tokens.v2.json. Print-friendly
 * (the @media print rules on the surrounding page strip chrome and let the
 * card fall onto one printed page).
 *
 * Sourced from design/mockups/V1_Candidate_Breakdown_Card.html (LOCKED).
 */

import HedgeMark from "./HedgeMark.jsx";

export default function CandidateBreakdownCard({
  candidateName,
  roleLabel,
  hiringManagerLabel,
  cardSections,
  engineVersion,
  consentRecord,
  blueprintId,
}) {
  if (!cardSections) {
    return (
      <div
        style={{
          padding: 24,
          fontFamily: "var(--ppr-typography-font-family-body)",
          color: "var(--ppr-color-text-muted)",
          fontStyle: "italic",
        }}
      >
        Card view unavailable — this breakdown was generated before the card schema landed.
        Switch to the full briefing.
      </div>
    );
  }

  const {
    snapshot,
    strengths = [],
    friction_points = [],
    energy_gainers = [],
    energy_drainers = [],
    how_to_manage,
  } = cardSections;

  return (
    <article style={cardShellStyle} data-component="CandidateBreakdownCard">
      <p style={eyebrowStyle}>Counterpart Breakdown · Card</p>
      <h1 style={nameStyle}>{candidateName || "Counterpart"}</h1>
      <p style={metaStyle}>
        {roleLabel ? <strong style={{ color: "var(--ppr-color-accent-navy)" }}>{roleLabel}</strong> : null}
        {roleLabel && hiringManagerLabel ? " · " : null}
        {hiringManagerLabel ? <>for {hiringManagerLabel}</> : null}
      </p>

      {/* Snapshot — full width */}
      <section style={sectionStyle}>
        <h2 style={sectionHeaderStyle}>Snapshot</h2>
        <p style={snapshotBodyStyle}>
          <HedgeMark>{snapshot}</HedgeMark>
        </p>
      </section>

      {/* Strengths + Friction — 2-col */}
      <section style={sectionStyle}>
        <div style={twoColStyle}>
          <div style={{ ...colPanelStyle, ...strengthsPanelStyle }}>
            <h3 style={sectionHeaderStyle}>Strengths</h3>
            <ol style={colListStyle}>
              {strengths.map((s, i) => (
                <li key={i} style={colListItemStyle}>
                  <span style={colListNumStyle}>{i + 1}</span>
                  <span>
                    <strong style={{ color: "var(--ppr-color-accent-navy)" }}>{s.headline}.</strong>{" "}
                    <HedgeMark>{s.body}</HedgeMark>
                  </span>
                </li>
              ))}
            </ol>
          </div>
          <div style={{ ...colPanelStyle, ...frictionPanelStyle }}>
            <h3 style={sectionHeaderStyle}>Friction points + blind spots</h3>
            <ol style={colListStyle}>
              {friction_points.map((s, i) => (
                <li key={i} style={colListItemStyle}>
                  <span style={colListNumStyle}>{i + 1}</span>
                  <span>
                    <strong style={{ color: "var(--ppr-color-accent-navy)" }}>{s.headline}.</strong>{" "}
                    <HedgeMark>{s.body}</HedgeMark>
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Energy gainers / drainers */}
      <section style={sectionStyle}>
        <h2 style={sectionHeaderStyle}>Energy gainers / drainers</h2>
        <div style={twoColStyle}>
          <div style={energyBoxStyle}>
            <h3 style={energyHeaderStyle}>Gainers</h3>
            <ul style={energyListStyle}>
              {energy_gainers.map((item, i) => (
                <li key={i} style={energyListItemStyle}>{item}</li>
              ))}
            </ul>
          </div>
          <div style={{ ...energyBoxStyle, ...energyDrainersBoxStyle }}>
            <h3 style={{ ...energyHeaderStyle, ...energyDrainersHeaderStyle }}>Drainers</h3>
            <ul style={energyListStyle}>
              {energy_drainers.map((item, i) => (
                <li
                  key={i}
                  style={{ ...energyListItemStyle, ...energyDrainersListItemStyle }}
                >
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* How to manage them */}
      <section style={sectionStyle}>
        <h2 style={sectionHeaderStyle}>How to manage them</h2>
        <div style={manageBlockStyle}>
          <p style={manageBodyStyle}>
            <HedgeMark>{how_to_manage}</HedgeMark>
          </p>
        </div>
      </section>

      {/* Audit footer — the four-field strip. The "Not for use in hiring
          decisions" line is dropped per LOCKED 2026-05-25 spec. */}
      <div style={auditFooterStyle}>
        {engineVersion ? <span><strong style={{ color: "var(--ppr-color-text-secondary)", fontWeight: 600 }}>engine:</strong> {engineVersion}</span> : null}
        {consentRecord ? <span><strong style={{ color: "var(--ppr-color-text-secondary)", fontWeight: 600 }}>consent:</strong> {consentRecord}</span> : null}
        {blueprintId ? <span><strong style={{ color: "var(--ppr-color-text-secondary)", fontWeight: 600 }}>id:</strong> {blueprintId}</span> : null}
        <span style={{ marginLeft: "auto" }}><strong style={{ color: "var(--ppr-color-text-secondary)", fontWeight: 600 }}>page</strong> 1 / 1</span>
      </div>
    </article>
  );
}

// ── styles ───────────────────────────────────────────────────────────────

const cardShellStyle = {
  maxWidth: 880,
  margin: "0 auto",
  background: "var(--ppr-color-background-surface)",
  border: "1px solid var(--ppr-color-border-default)",
  borderRadius: 6,
  padding: "26px 30px 24px",
};

const eyebrowStyle = {
  fontFamily: "var(--ppr-typography-font-family-mono)",
  fontSize: 10,
  letterSpacing: "0.10em",
  textTransform: "uppercase",
  color: "var(--ppr-color-accent-coral-dark)",
  margin: "0 0 8px",
  fontWeight: 600,
};

const nameStyle = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: 30,
  lineHeight: 1.05,
  letterSpacing: "-0.02em",
  color: "var(--ppr-color-accent-navy)",
  margin: "0 0 4px",
};

const metaStyle = {
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: 13,
  color: "var(--ppr-color-text-secondary)",
  margin: "0 0 18px",
  paddingBottom: 16,
  borderBottom: "1px solid var(--ppr-color-border-default)",
};

const sectionStyle = { marginTop: 16 };

const sectionHeaderStyle = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: 12,
  letterSpacing: "0.10em",
  textTransform: "uppercase",
  color: "var(--ppr-color-accent-navy)",
  margin: "0 0 8px",
};

const snapshotBodyStyle = {
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: 14.5,
  lineHeight: 1.6,
  color: "var(--ppr-color-text-primary)",
  margin: 0,
};

const twoColStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 18,
  marginTop: 4,
};

const colPanelStyle = {
  padding: "14px 16px",
  border: "1.5px solid var(--ppr-color-border-default)",
  borderRadius: 5,
};

const strengthsPanelStyle = {
  background: "var(--ppr-color-accent-pale-soft)",
  borderLeft: "4px solid var(--ppr-color-accent-steel)",
};

const frictionPanelStyle = {
  background: "var(--ppr-color-accent-coral-very-soft)",
  borderLeft: "4px solid var(--ppr-color-accent-coral)",
};

const colListStyle = { listStyle: "none", margin: 0, padding: 0 };

const colListItemStyle = {
  display: "grid",
  gridTemplateColumns: "18px 1fr",
  gap: 9,
  padding: "4px 0",
  alignItems: "start",
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: 13,
  lineHeight: 1.5,
  color: "var(--ppr-color-text-primary)",
};

const colListNumStyle = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: 11,
  color: "var(--ppr-color-accent-navy)",
};

const energyBoxStyle = {
  padding: "12px 14px",
  border: "1px solid var(--ppr-color-border-default)",
  borderRadius: 5,
  background: "var(--ppr-color-background-sub)",
};

const energyDrainersBoxStyle = {};

const energyHeaderStyle = {
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  color: "var(--ppr-color-accent-steel-dark)",
  margin: "0 0 6px",
};

const energyDrainersHeaderStyle = { color: "var(--ppr-color-accent-coral-dark)" };

const energyListStyle = { listStyle: "none", margin: 0, padding: 0 };

const energyListItemStyle = {
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: 12.5,
  lineHeight: 1.5,
  color: "var(--ppr-color-text-primary)",
  padding: "2px 0 2px 14px",
  position: "relative",
};

const energyDrainersListItemStyle = {};

const manageBlockStyle = {
  marginTop: 14,
  padding: "14px 16px",
  border: "1px solid var(--ppr-color-border-default)",
  borderRadius: 5,
  background: "var(--ppr-color-background-surface)",
};

const manageBodyStyle = {
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: 13.5,
  lineHeight: 1.6,
  color: "var(--ppr-color-text-primary)",
  margin: 0,
};

const auditFooterStyle = {
  marginTop: 16,
  paddingTop: 12,
  borderTop: "1px solid var(--ppr-color-border-default)",
  display: "flex",
  flexWrap: "wrap",
  gap: 16,
  fontFamily: "var(--ppr-typography-font-family-mono)",
  fontSize: 10,
  color: "var(--ppr-color-text-muted)",
  letterSpacing: "0.03em",
};
